import { useEffect, useState } from 'react';

interface UseScrollToBottomOptions {
    /** CSS selector for the scrollable container */
    containerSelector?: string;
    /** Tolerance in pixels for detecting bottom (default: 10) */
    tolerance?: number;
    /** Initial delay before setting up listeners (default: 100ms) */
    initialDelay?: number;
    /** Delay for initial state check (default: 150ms) */
    stateCheckDelay?: number;
    /** Retry interval when container not found (default: 100ms) */
    retryInterval?: number;
    /** Initial state - true for clear transparent, false for glossy translucent (default: true) */
    initialState?: boolean;
}

/**
 * Custom hook to detect when a scrollable container has reached the bottom
 * or when no scrolling is needed (content fits in viewport)
 * 
 * @param options Configuration options
 * @returns boolean indicating if at bottom or no scroll needed
 */
export const useScrollToBottom = (options: UseScrollToBottomOptions = {}) => {
    const {
        containerSelector = '.main-content-wrapper',
        tolerance = 10,
        initialDelay = 100,
        stateCheckDelay = 150,
        retryInterval = 100,
        initialState = true
    } = options;

    const [isAtBottom, setIsAtBottom] = useState(initialState);

    useEffect(() => {
        const handleScroll = () => {
            const container = document.querySelector(containerSelector);
            if (container) {
                const { scrollTop, scrollHeight, clientHeight } = container;
                const isAtEnd = scrollTop + clientHeight >= scrollHeight - tolerance;
                const hasNoScroll = scrollHeight <= clientHeight;
                
                setIsAtBottom(isAtEnd || hasNoScroll);
            }
        };

        let cleanup: (() => void) | null = null;

        // Use requestAnimationFrame to ensure DOM is ready
        const setupListener = () => {
            requestAnimationFrame(() => {
                const container = document.querySelector(containerSelector);
                if (container) {
                    container.addEventListener('scroll', handleScroll);
                    // Check initial state after a brief delay
                    setTimeout(handleScroll, stateCheckDelay);
                    
                    cleanup = () => {
                        container.removeEventListener('scroll', handleScroll);
                    };
                } else {
                    // Retry if element not found
                    setTimeout(setupListener, retryInterval);
                }
            });
        };

        // Setup with initial delay
        const timeoutId = setTimeout(setupListener, initialDelay);

        return () => {
            clearTimeout(timeoutId);
            if (cleanup) {
                cleanup();
            }
        };
    }, [containerSelector, tolerance, initialDelay, stateCheckDelay, retryInterval]);

    return isAtBottom;
}; 